import { map, tap } from 'rxjs/operators';
import { concatMap, Observable, of } from 'rxjs';
import { computed, inject, Injectable, signal } from '@angular/core';
import { clone } from '@factories/helpers';
import { IBackground, IClientConfiguration, ITheme } from '@interfaces/nebula';
import { SharedataService } from './sharedata.service';
import { ConfigurationService } from './configuration.service';
import { RetroSoundService } from './retro-sound.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private data = inject(SharedataService);
  private configurationService = inject(ConfigurationService);
  private retroSound = inject(RetroSoundService);

  readonly currentTheme = signal<string>('');
  readonly isRetroTheme = computed(() => this.currentTheme() === 'theme-retro');
  private readonly themesList: ITheme[] = [
    {
      name: 'Carbon',
      cssClass: 'theme-carbon',
      imageSrc: '/assets/img/theme-pic/carbon.jpg'
    },
    {
      name: 'Clear',
      cssClass: 'theme-clear',
      imageSrc: '/assets/img/theme-pic/clear.jpg'
    },
    {
      name: 'Dark',
      cssClass: 'theme-dark',
      imageSrc: '/assets/img/theme-pic/dark.jpg'
    },
    {
      name: 'Dark Moon',
      cssClass: 'theme-dark-moon',
      imageSrc: '/assets/img/theme-pic/dark-moon.jpg'
    },
    {
      name: 'Dark Turquoise',
      cssClass: 'theme-dark-turquoise',
      imageSrc: '/assets/img/theme-pic/dark-turquoise.jpg'
    },
    {
      name: 'Lava',
      cssClass: 'theme-lava',
      imageSrc: '/assets/img/theme-pic/lava.jpg'
    },
    {
      name: 'Light Blue',
      cssClass: 'theme-light-blue',
      imageSrc: '/assets/img/theme-pic/light-blue.jpg'
    },
    {
      name: 'Light Green',
      cssClass: 'theme-light-green',
      imageSrc: '/assets/img/theme-pic/light-green.jpg'
    },
    {
      name: 'Light Red',
      cssClass: 'theme-light-red',
      imageSrc: '/assets/img/theme-pic/light-red.jpg'
    },
    {
      name: 'Retro',
      cssClass: 'theme-retro',
      imageSrc: '/assets/img/theme-pic/retro.jpg'
    },
    {
      name: 'Sky',
      cssClass: 'theme-sky',
      imageSrc: '/assets/img/theme-pic/sky.jpg'
    }
  ];

  private readonly backgroundsList: IBackground[] = [
    {
      title: 'Bamboo',
      background: 'bamboo',
      backgroundSrc: '/assets/img/background-pic/bamboo.jpg',
      previewSrc: '/assets/img/background-pic/bamboo_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Calla',
      background: 'calla',
      backgroundSrc: '/assets/img/background-pic/calla.jpg',
      previewSrc: '/assets/img/background-pic/calla_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Christmas',
      background: 'christmas',
      backgroundSrc: '/assets/img/background-pic/christmas.jpg',
      previewSrc: '/assets/img/background-pic/christmas_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Court',
      background: 'court',
      backgroundSrc: '/assets/img/background-pic/court.jpg',
      previewSrc: '/assets/img/background-pic/court_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Facade',
      background: 'facade',
      backgroundSrc: '/assets/img/background-pic/facade.jpg',
      previewSrc: '/assets/img/background-pic/facade_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Graffiti',
      background: 'graffiti',
      backgroundSrc: '/assets/img/background-pic/graffiti.jpg',
      previewSrc: '/assets/img/background-pic/graffiti_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Graffiti 2',
      background: 'graffiti_2',
      backgroundSrc: '/assets/img/background-pic/graffiti_2.jpg',
      previewSrc: '/assets/img/background-pic/graffiti_2_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Graffiti 3',
      background: 'graffiti_3',
      backgroundSrc: '/assets/img/background-pic/graffiti_3.jpg',
      previewSrc: '/assets/img/background-pic/graffiti_3_prev.jpg',
      colorMode: 'light'
    },

    {
      title: 'Jellyfish',
      background: 'jellyfish',
      backgroundSrc: '/assets/img/background-pic/jellyfish.jpg',
      previewSrc: '/assets/img/background-pic/jellyfish_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Lily of the valley',
      background: 'lily-of-the-valley',
      backgroundSrc: '/assets/img/background-pic/lily-of-the-valley.jpg',
      previewSrc: '/assets/img/background-pic/lily-of-the-valley_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Maybach',
      background: 'maybach',
      backgroundSrc: '/assets/img/background-pic/maybach.jpg',
      previewSrc: '/assets/img/background-pic/maybach_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Monk',
      background: 'monk',
      backgroundSrc: '/assets/img/background-pic/monk.jpg',
      previewSrc: '/assets/img/background-pic/monk_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Mushrooms',
      background: 'mushrooms',
      backgroundSrc: '/assets/img/background-pic/mushrooms.jpg',
      previewSrc: '/assets/img/background-pic/mushrooms_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Origami',
      background: 'origami',
      backgroundSrc: '/assets/img/background-pic/origami.jpg',
      previewSrc: '/assets/img/background-pic/origami_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Pampas',
      background: 'pampas',
      backgroundSrc: '/assets/img/background-pic/pampas.jpg',
      previewSrc: '/assets/img/background-pic/pampas_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Pattern',
      background: 'pattern',
      backgroundSrc: '/assets/img/background-pic/pattern.jpg',
      previewSrc: '/assets/img/background-pic/pattern_prev.jpg',
      colorMode: 'dark'
    },
    {
      title: 'Pattern 2',
      background: 'pattern_2',
      backgroundSrc: '/assets/img/background-pic/pattern_2.jpg',
      previewSrc: '/assets/img/background-pic/pattern_2_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Pattern 3',
      background: 'pattern_3',
      backgroundSrc: '/assets/img/background-pic/pattern_3.jpg',
      previewSrc: '/assets/img/background-pic/pattern_3_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Pattern 4',
      background: 'pattern_4',
      backgroundSrc: '/assets/img/background-pic/pattern_4.jpg',
      previewSrc: '/assets/img/background-pic/pattern_4_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Pattern 5',
      background: 'pattern_5',
      backgroundSrc: '/assets/img/background-pic/pattern_5.jpg',
      previewSrc: '/assets/img/background-pic/pattern_5_prev.jpg',
      colorMode: 'dark'
    },
    {
      title: 'Pattern 6',
      background: 'pattern_6',
      backgroundSrc: '/assets/img/background-pic/pattern_6.jpg',
      previewSrc: '/assets/img/background-pic/pattern_6_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Pattern 7',
      background: 'pattern_7',
      backgroundSrc: '/assets/img/background-pic/pattern_7.jpg',
      previewSrc: '/assets/img/background-pic/pattern_7_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Pattern 8',
      background: 'pattern_8',
      backgroundSrc: '/assets/img/background-pic/pattern_8.jpg',
      previewSrc: '/assets/img/background-pic/pattern_8_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Pattern 9',
      background: 'pattern_9',
      backgroundSrc: '/assets/img/background-pic/pattern_9.jpg',
      previewSrc: '/assets/img/background-pic/pattern_9_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Porsche',
      background: 'porsche',
      backgroundSrc: '/assets/img/background-pic/porsche.jpg',
      previewSrc: '/assets/img/background-pic/porsche_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Regatta',
      background: 'regatta',
      backgroundSrc: '/assets/img/background-pic/regatta.jpg',
      previewSrc: '/assets/img/background-pic/regatta_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Retro Teal',
      background: 'retro-teal',
      backgroundSrc: '/assets/img/background-pic/retro_teal.svg',
      previewSrc: '/assets/img/background-pic/retro_teal_prev.svg',
      colorMode: 'light'
    },
    {
      title: 'Road',
      background: 'road',
      backgroundSrc: '/assets/img/background-pic/road.jpg',
      previewSrc: '/assets/img/background-pic/road_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Rowing',
      background: 'rowing',
      backgroundSrc: '/assets/img/background-pic/rowing.jpg',
      previewSrc: '/assets/img/background-pic/rowing_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Ship',
      background: 'ship',
      backgroundSrc: '/assets/img/background-pic/ship.jpg',
      previewSrc: '/assets/img/background-pic/ship_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Space',
      background: 'space',
      backgroundSrc: '/assets/img/background-pic/space.jpg',
      previewSrc: '/assets/img/background-pic/space_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Space 2',
      background: 'space 2',
      backgroundSrc: '/assets/img/background-pic/space_2.jpg',
      previewSrc: '/assets/img/background-pic/space_2_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Sunflower',
      background: 'sunflower',
      backgroundSrc: '/assets/img/background-pic/sunflower.jpg',
      previewSrc: '/assets/img/background-pic/sunflower_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Sunrise',
      background: 'sunrise',
      backgroundSrc: '/assets/img/background-pic/sunrise.jpg',
      previewSrc: '/assets/img/background-pic/sunrise_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Sunset',
      background: 'sunset',
      backgroundSrc: '/assets/img/background-pic/sunset.jpg',
      previewSrc: '/assets/img/background-pic/sunset_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Time capsule',
      background: 'time capsule',
      backgroundSrc: '/assets/img/background-pic/time_capsule.jpg',
      previewSrc: '/assets/img/background-pic/time_capsule_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Urban',
      background: 'urban',
      backgroundSrc: '/assets/img/background-pic/urban.jpg',
      previewSrc: '/assets/img/background-pic/urban_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Zen',
      background: 'zen',
      backgroundSrc: '/assets/img/background-pic/zen.jpg',
      previewSrc: '/assets/img/background-pic/zen_prev.jpg',
      colorMode: 'light'
    }
  ];

  private readonly startPageBackgroundsList: IBackground[] = [
    {
      title: 'Coming times',
      background: 'coming times',
      backgroundSrc: '/assets/img/background-pic/coming_times.jpg',
      previewSrc: '/assets/img/background-pic/coming_times_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Curved Paper',
      background: 'curved paper',
      backgroundSrc: '/assets/img/background-pic/curved_paper.jpg',
      previewSrc: '/assets/img/background-pic/curved_paper_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Finding',
      background: 'finding',
      backgroundSrc: '/assets/img/background-pic/finding.jpg',
      previewSrc: '/assets/img/background-pic/finding_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Meetingroom',
      background: 'meetingroom',
      backgroundSrc: '/assets/img/background-pic/meetingroom.jpg',
      previewSrc: '/assets/img/background-pic/meetingroom_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Red Couch',
      background: 'red couch',
      backgroundSrc: '/assets/img/background-pic/red_couch.jpg',
      previewSrc: '/assets/img/background-pic/red_couch_prev.jpg',
      colorMode: 'dark'
    },
    {
      title: 'Space Bridge',
      background: 'space bridge',
      backgroundSrc: '/assets/img/background-pic/space_bridge.jpg',
      previewSrc: '/assets/img/background-pic/space_bridge_prev.jpg',
      colorMode: 'dark'
    },
    {
      title: 'Space Livingroom',
      background: 'space livingroom',
      backgroundSrc: '/assets/img/background-pic/space_livingroom.jpg',
      previewSrc: '/assets/img/background-pic/space_livingroom_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Space Office',
      background: 'space office',
      backgroundSrc: '/assets/img/background-pic/space_office.jpg',
      previewSrc: '/assets/img/background-pic/space_office_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Sunsetbeach',
      background: 'sunsetbeach',
      backgroundSrc: '/assets/img/background-pic/sunsetbeach.jpg',
      previewSrc: '/assets/img/background-pic/sunsetbeach_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Work in Progress',
      background: 'work in progress',
      backgroundSrc: '/assets/img/background-pic/work_in_progress.jpg',
      previewSrc: '/assets/img/background-pic/work_in_progress_prev.jpg',
      colorMode: 'dark'
    },
    {
      title: 'Workingtable',
      background: 'workingtable',
      backgroundSrc: '/assets/img/background-pic/workingtable.jpg',
      previewSrc: '/assets/img/background-pic/workingtable_prev.jpg',
      colorMode: 'light'
    },
    {
      title: 'Zen garden',
      background: 'zen garden',
      backgroundSrc: '/assets/img/background-pic/zen_garden.jpg',
      previewSrc: '/assets/img/background-pic/zen_garden_prev.jpg',
      colorMode: 'light'
    }
  ];

  private themeToBackgroundMap = new Map([
    ['theme-light-blue', 'graffiti'],
    ['theme-light-green', 'graffiti_3'],
    ['theme-light-red', 'porsche'],
    ['theme-dark-moon', 'jellyfish'],
    ['theme-clear', 'pattern_2'],
    ['theme-sky', 'pampas'],
    ['theme-lava', 'ship'],
    ['theme-dark-turquoise', 'pattern_5'],
    ['theme-carbon', 'maybach'],
    ['theme-agilicision', 'space'],
    ['theme-retro', 'retro-teal'],
    ['theme-dark', 'sunset']
  ]);

  private readonly backgroundMigrations: Record<string, string> = {
    bg: 'pattern_9'
  };

  private readonly DEFAULT_BACKGROUND_ID = 'pattern_8';

  private retroIconObserver?: MutationObserver;

  applyTheme(theme: string): void {
    const previousTheme = this.currentTheme();
    const body = document.getElementsByTagName('body')[0];
    if (previousTheme) {
      body?.classList.remove(previousTheme);
    }

    if (theme) {
      body?.classList.add(theme);
    }
    const isThemeSwitch = Boolean(previousTheme && previousTheme !== theme);

    this.currentTheme.set(theme);

    if (theme === 'theme-retro') {
      this.initRetroIcons();
      this.retroSound.enable(isThemeSwitch);
    } else {
      this.cleanupRetroIcons();
      this.retroSound.disable();
    }
  }

  private initRetroIcons(): void {
    const tagHeaderIcons = () => {
      const headerContainers = document.querySelectorAll<HTMLElement>(
        'header, .header, app-header-container, .navbar, #createOf, .dashboard_toggler'
      );
      headerContainers.forEach(header => {
        const icons = header.querySelectorAll<HTMLElement>(
          '.material-icons, .material-symbols-outlined, [class*="ion-"]'
        );
        icons.forEach(el => {
          let iconName = '';
          for (let i = 0; i < el.childNodes.length; i++) {
            const node = el.childNodes[i];
            if (node.nodeType === Node.TEXT_NODE) {
              const val = node.nodeValue?.trim();
              if (val) {
                iconName = val.split(/\s+/)[0].toLowerCase();
                break;
              }
            }
          }
          if (!iconName && el.textContent) {
            iconName = el.textContent.trim().split(/\s+/)[0].toLowerCase();
          }
          if (iconName && el.getAttribute('data-icon') !== iconName) {
            el.setAttribute('data-icon', iconName);
          }
        });
      });
    };

    tagHeaderIcons();

    if (!this.retroIconObserver && typeof MutationObserver !== 'undefined') {
      this.retroIconObserver = new MutationObserver(() => {
        tagHeaderIcons();
      });
      this.retroIconObserver.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
  }

  private cleanupRetroIcons(): void {
    if (this.retroIconObserver) {
      this.retroIconObserver.disconnect();
      this.retroIconObserver = undefined;
    }
    document.querySelectorAll('[data-icon]').forEach(el => el.removeAttribute('data-icon'));
  }

  createConfiguration(): IClientConfiguration {
    return {
      logo: null,
      background: null
    };
  }

  getBackgrounds(): IBackground[] {
    return clone(this.backgroundsList);
  }

  getStartPageBackgrounds(): IBackground[] {
    return clone(this.startPageBackgroundsList);
  }

  getAllBackgrounds(): IBackground[] {
    return [...this.backgroundsList, ...this.startPageBackgroundsList];
  }

  getBackground(): Observable<string> {
    return this.configurationService.getBackgroundImage().pipe(
      concatMap(background => {
        if (background) {
          this.data.customBackground.set(background);
          this.data.isCustomBackground.set(true);
          return of(background);
        }
        return this.configurationService.resolveAppBackgroud();
      }),
      concatMap(background => {
        const migratedId = this.backgroundMigrations[background];
        const isMigrated = !!migratedId;
        if (isMigrated) {
          this.data.customBackground.set(migratedId);
          return this.setBackground(migratedId);
        }
        let item = this.backgroundsList.find(bg => bg.background === background);
        const isCustom = this.data.isCustomBackground();
        if (!item && !isCustom) {
          const defaultId = this.DEFAULT_BACKGROUND_ID;
          item = this.backgroundsList.find(bg => bg.background === defaultId) || this.backgroundsList[0];
          this.data.customBackground.set(item.background);
          return this.setBackground(item.background);
        }
        const url = item ? item.backgroundSrc : background;
        return of(url);
      })
    );
  }

  getThemes(): ITheme[] {
    return clone(this.themesList);
  }

  getThemeBackground(theme: string): string {
    return this.themeToBackgroundMap.get(theme) || 'pattern';
  }

  getAppTheme(): Observable<string> {
    return this.configurationService.getWebAppDesign('user').pipe(map(theme => theme || 'theme-clear'));
  }

  /**
   * set background
   */
  private setBackground(background: string): Observable<string> {
    return this.configurationService.setWebAppBackgroundImageName(background, 'user').pipe(
      map(() => {
        const item = this.backgroundsList.find(bg => bg.background === background);
        return item ? item.backgroundSrc : background;
      }),
      tap({
        next: url => {
          this.data.background.set(url);
        }
      })
    );
  }
}
